import {Action, Module, Mutation, MutationAction, VuexModule} from "vuex-module-decorators";
import {CompetitionRef} from "@/modules/competition/service/CompetitionService";
import {
    BindingSummary,
    CompetitionTestBinding,
    manageStartlistService,
    RegistrationRequest,
    StartlistsMenu
} from "@/modules/competition/service/ManageStartlistService";
import {StartlistEntryRef, StartlistGroupRef, StartlistRef} from "@/modules/competition/service/ManageResultsService";

@Module({namespaced: true, name: 'competition-manage-startlist'})
export default class CompetitionManageStartlistModule extends VuexModule {
    menu: StartlistsMenu = {} as StartlistsMenu
    testsWithoutStartlistsCount: number = 0
    hasAutomaticStartlists: boolean = false
    binding: CompetitionTestBinding = {} as CompetitionTestBinding;

    @Mutation
    clearStore() {
        this.menu = {} as StartlistsMenu
        this.binding = {} as CompetitionTestBinding
        this.hasAutomaticStartlists = false
    }

    @MutationAction
    async loadData(ref: CompetitionRef) {
        const data = await manageStartlistService.getData(ref)
        return {
            menu: data.menu,
            testsWithoutStartlistsCount: data.testsWithoutStartlistsCount,
            hasAutomaticStartlists: data.hasAutomaticStartlists
        }
    }

    @MutationAction
    async select(request: SelectRequest) {
        const data = await manageStartlistService.getTestBinding(request.competitionRef, request.bindingRef)
        return {binding: data}
    }

    @Mutation
    clearSelection() {
        this.binding = {} as CompetitionTestBinding
    }

    @Mutation
    async refresh(competitionRef: CompetitionRef) {
        this.binding = await manageStartlistService.getTestBinding(competitionRef, this.binding.ref)
    }

    @MutationAction
    async load(request: LoadRequest) {
        if (request.bindingRef) {
            const data = await manageStartlistService.getTestBinding(request.competitionRef, request.bindingRef)
            return {binding: data}
        }
        return {}
    }

    @Action
    async addAllRegistrations(request: AddAllRegistrationRequest) {
        const update = await manageStartlistService.addAllRegistrations(request.competition, request.startlistRef)
    }

    @Action
    async addRegistrations(request: AddRegistrationRequest) {
        const update = await manageStartlistService.addRegistrations(request.competition, request.startlistRef, request.registrations)
        updateMenuBinding(this.menu, update)
    }

    @Action
    async addBreak(request: AddBreakRequest) {
        const update = await manageStartlistService.addBreak(request.competition, request.startlistRef, request.breakType, request.order, request.duration)
        updateMenuBinding(this.menu, update)
    }

    @Action
    async addOpenPosition(request: AddOpenPositionRequest) {
        const update = await manageStartlistService.addOpenPosition(request.competition, request.startlistRef, request.position, request.quantity)
        updateMenuBinding(this.menu, update)
    }

    @Action
    async removeEntry(request: RemoveEntryRequest) {
        const update = await manageStartlistService.removeEntry(request.competition, request.startlistRef, request.startlistEntryRef)
        updateMenuBinding(this.menu, update)
    }

    @Action
    async removeEntries(request: RemoveEntriesRequest) {
        const update = await manageStartlistService.removeEntries(request.competition, request.startlistRef, request.startlistEntryRefs, request.softDelete)
        updateMenuBinding(this.menu, update)
    }
}

export function updateMenuBinding(menu: StartlistsMenu, summary: BindingSummary) {
    menu.entries.find(e => {
        const m = e.entries.find(tb => tb.ref == summary.ref)
        if (m && m.startlist) {
            m.startlist.participantCount = summary.participantCount
            m.startlist.capacity = summary.capacity
        }
    })
}

export interface SelectRequest {
    competitionRef: CompetitionRef,
    bindingRef: StartlistGroupRef,
}

export interface LoadRequest {
    competitionRef: CompetitionRef,
    bindingRef?: StartlistGroupRef
}

export interface AddAllRegistrationRequest {
    competition: CompetitionRef,
    startlistRef: StartlistRef,
}

export interface AddRegistrationRequest {
    competition: CompetitionRef,
    startlistRef: StartlistRef,
    registrations: Array<RegistrationRequest>
}

export interface AddBreakRequest {
    competition: CompetitionRef,
    startlistRef: StartlistRef,
    breakType: string,
    order: number,
    duration: number
}

export interface AddOpenPositionRequest {
    competition: CompetitionRef,
    startlistRef: StartlistRef,
    position: number,
    quantity: number
}

export interface RemoveEntryRequest {
    competition: CompetitionRef,
    startlistRef: StartlistRef,
    startlistEntryRef: StartlistEntryRef
}

export interface RemoveEntriesRequest {
    competition: CompetitionRef,
    startlistRef: StartlistRef,
    startlistEntryRefs: StartlistEntryRef[],
    softDelete: boolean
}
